import React, { useCallback, useState } from 'react';

import { Autocomplete, Button, FormGroup, MenuItem, Stack, TextField } from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import { LE_AGENT_HUB } from '~/consts/brands';

import { Affiliation, Eoi, UpdateEoiDetailsParams } from '~/types/services/agentHub';

interface Props {
  eoiData: Eoi;
  onCancel: () => void;
  availableAffiliations: Array<Affiliation>;
  onSave: (data: UpdateEoiDetailsParams) => void;
  isSaving: boolean;
}

export default function AgencySubmissionEditor({ eoiData, onCancel, availableAffiliations, onSave, isSaving }: Props) {
  const [editedData, setEditedData] = useState<Partial<UpdateEoiDetailsParams>>({
    agencyName: eoiData.agencyName,
    regionCode: eoiData.regionCode,
    affiliationId: eoiData.affiliationId,
    givenName: eoiData.givenName,
    surname: eoiData.surname,
    email: eoiData.email,
    phone: eoiData.phone,
  });

  const availableRegions = getRegions(LE_AGENT_HUB);

  const handleAgencyUpdate = useCallback(() => {
    onSave(editedData);
  }, [editedData, onSave]);

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()} style={{ minWidth: '500px' }}>
        <Stack mt={2} direction="column" spacing={2}>
          <FormGroup>
            <TextField
              label="Agency Name"
              value={editedData.agencyName}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, agencyName: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              select
              label="Region"
              fullWidth
              required
              value={editedData.regionCode}
              onChange={(e) =>
                setEditedData({ ...editedData, regionCode: e.target.value as UpdateEoiDetailsParams['regionCode'] })
              }
            >
              {availableRegions.map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  {region.name}
                </MenuItem>
              ))}
            </TextField>
          </FormGroup>
          <FormGroup>
            <Autocomplete
              value={availableAffiliations?.find((affiliation) => affiliation.id === editedData?.affiliationId)}
              options={availableAffiliations}
              getOptionLabel={(option) => `${option.region} - ${option.name}`}
              onChange={(_, value) => setEditedData({ ...editedData, affiliationId: value ? value.id : '' })}
              renderInput={(params) => <TextField {...params} required label="Affiliation" />}
            />
          </FormGroup>
          <FormGroup row sx={{ gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
            <TextField
              label="First Name"
              value={editedData.givenName}
              required
              sx={{ flex: 1 }}
              onChange={(e) => setEditedData({ ...editedData, givenName: e.target.value })}
            />

            <TextField
              label="Last Name"
              value={editedData.surname}
              required
              sx={{ flex: 1 }}
              onChange={(e) => setEditedData({ ...editedData, surname: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label="Email"
              value={editedData.email}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label="Phone"
              value={editedData.phone}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })}
            />
          </FormGroup>
        </Stack>
      </form>
      <Stack mt={2} display="flex" justifyContent="space-around" direction="row">
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAgencyUpdate} loading={isSaving}>
          Save
        </Button>
      </Stack>
    </>
  );
}
